import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { CreditCard, X } from 'lucide-react'
import type { RootState } from '../store'
import { useGetUsageQuery } from '../services/projectDataApi'
import { useNavigate } from './nav'
import { useEmbedded } from '../lib/embed'
import { isDevMock } from '../dev/isDevMock'
import { trackEvent } from '../lib/analytics'
import { trackSegmentEvent } from '../lib/segment'
import ModalPortal from './ModalPortal'
import ScheduleCallCta from './ScheduleCallCta'
import { isSuppressedPath } from '../lib/outOfCreditsGate'
import './OutOfCreditsModal.css'

// Remembered for the tab session so a user who dismisses the nudge isn't confronted again on every
// navigation (and so it never wedges automated flows). sessionStorage clears when the tab closes, so
// a still-empty account is reminded again next visit.
const DISMISSED_KEY = 'out-of-credits-modal-dismissed'

// The Inter face is declared by the Google Fonts stylesheet in index.html (variable wght 300..700,
// display=optional) and its woff2 is fetched on first use: text first painted before the fetch
// completes is laid out with fallback metrics, and when the face applies afterwards it re-flows
// that text — the measured 9px 'View plans' shift. The modal opens long after load, so its first
// paint usually just needs the face to be settled; holding that first render until it is lays the
// label out with final metrics in the same paint, and nothing already settled moves afterwards.
const INTER_FACE_QUERY = '600 14px Inter'

// Generous against the ~140ms fetch observed in the recording, and short enough that a stalled
// font download can only delay the nudge, never hide it.
const FONT_SETTLE_TIMEOUT_MS = 2000

/**
 * Resolves once the Inter face is settled: `true` when it is usable (the modal's first paint will
 * use final metrics), `false` when the wait gave up — the document's font loading ended without
 * the face (a failed face never swaps in, so there is nothing left to re-flow) or the timeout
 * fired.
 */
async function waitForInterFace(fonts: FontFaceSet, timeoutMs: number): Promise<boolean> {
  if (fonts.check(INTER_FACE_QUERY)) return true
  return new Promise<boolean>((resolve) => {
    const done = (settled: boolean) => {
      if (timer !== undefined) clearTimeout(timer)
      resolve(settled)
    }
    const timer: ReturnType<typeof setTimeout> | undefined = setTimeout(
      () => done(false),
      timeoutMs,
    )
    fonts.ready.then(
      () => done(fonts.check(INTER_FACE_QUERY)),
      // fonts.ready itself rejected: nothing more to learn from it, stop waiting.
      () => done(false),
    )
  })
}

/**
 * OutOfCreditsModal — a dismissible in-app modal shown when the user's ACCOUNT-level credit balance
 * hits zero, on the ordinary viewing surfaces where nothing else surfaces it (project pages,
 * insights, etc.). It complements the full-screen SubscriptionGate, which only blocks the
 * project-CREATION routes: this reaches a user who has run dry while simply browsing.
 *
 * It offers two paths forward — "View plans" (the /settings/subscription picker) and the 75%-off
 * "Schedule a call" offer (ScheduleCallCta, the same Calendly booking the out-of-credits email uses).
 * It is deliberately dismissible (close button, backdrop click, Escape) and remembers dismissal for
 * the tab session, so it informs without locking anyone (or any automation) out.
 *
 * Rendered once at the app root (App.tsx). Reads the same account-level usage the credit gate does
 * (useGetUsageQuery with no scope → the best-funded team the caller belongs to).
 */
export default function OutOfCreditsModal() {
  const location = useLocation()
  const navigate = useNavigate()
  const embedded = useEmbedded()
  const user = useSelector((s: RootState) => s.auth.user)
  const devMock = isDevMock()

  const usageQuery = useGetUsageQuery(undefined, { skip: !user || devMock })
  const outOfCredits = !!usageQuery.data && usageQuery.data.available_credits <= 0

  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISSED_KEY) === '1'
    } catch {
      return false
    }
  })

  const suppressed = embedded || isSuppressedPath(location.pathname)
  const open = outOfCredits && !dismissed && !suppressed

  const dismiss = useCallback(() => {
    setDismissed(true)
    try {
      sessionStorage.setItem(DISMISSED_KEY, '1')
    } catch {
      // A storage failure only means the nudge may reappear on the next navigation — harmless.
    }
    trackSegmentEvent('Dismissed out-of-credits modal')
  }, [])

  // Fire the "shown" analytics once per open, mirroring SubscriptionGate's paywall event so the same
  // funnel can attribute this surface too.
  const shownFiredRef = useRef(false)
  useEffect(() => {
    if (!open) {
      shownFiredRef.current = false
      return
    }
    if (shownFiredRef.current) return
    shownFiredRef.current = true
    trackEvent('out-of-credits-modal-shown')
    trackSegmentEvent('Shown out-of-credits modal')
  }, [open])

  // Escape to dismiss, matching the app's other dialogs.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, dismiss])

  // Hold the modal's FIRST render until the Inter face settles (see waitForInterFace): laying the
  // label out only once the face is usable is what keeps the actions row from re-flowing a settled
  // frame later. One boolean for the component's lifetime — a face that settles stays settled.
  const [fontSettled, setFontSettled] = useState(false)
  useEffect(() => {
    if (!open || fontSettled) return
    let cancelled = false
    void waitForInterFace(document.fonts, FONT_SETTLE_TIMEOUT_MS).then(() => {
      if (!cancelled) setFontSettled(true)
    })
    return () => {
      cancelled = true
    }
  }, [open, fontSettled])

  const viewPlans = useCallback(() => {
    trackEvent('upgrade-cta-clicked', { source: 'out-of-credits-modal' })
    trackSegmentEvent('Clicked View Plans', { source: 'out-of-credits-modal' })
    // Keep the current page as the modal's background so closing settings returns the user here.
    navigate('/settings/subscription', {
      state: { backgroundLocation: { pathname: location.pathname, search: location.search, hash: location.hash } },
    })
  }, [navigate, location.pathname, location.search, location.hash])

  if (!open || !fontSettled) return null

  return (
    <ModalPortal>
      <div
        className="out-of-credits-modal__overlay"
        onClick={dismiss}
        data-testid="out-of-credits-modal"
      >
        <div
          className="out-of-credits-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="out-of-credits-modal-title"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="out-of-credits-modal__close"
            onClick={dismiss}
            aria-label="Close"
            data-testid="out-of-credits-modal-close"
            data-analytics-event="out-of-credits-modal-close"
          >
            <X size={18} aria-hidden />
          </button>

          <div className="out-of-credits-modal__head">
            <div className="out-of-credits-modal__icon" aria-hidden>
              <CreditCard size={22} strokeWidth={1.75} />
            </div>
            <h1 id="out-of-credits-modal-title" className="out-of-credits-modal__title">
              You’re out of credits
            </h1>
            <p className="out-of-credits-modal__subtitle">
              Your account has run out of credits, so QA has paused. Upgrade your plan for more monthly
              credits and testing resumes right away — the bugs already found are saved and waiting.
            </p>
          </div>

          <ScheduleCallCta source="out-of-credits-modal" />

          <div className="out-of-credits-modal__actions">
            <button
              type="button"
              className="out-of-credits-modal__view-plans"
              onClick={viewPlans}
              data-analytics-event="out-of-credits-modal-view-plans"
              data-testid="out-of-credits-modal-view-plans"
            >
              View plans
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  )
}
